import { createContext, useContext, useState } from "react";
import {
  AllCommentsProps,
  BusniessOfficeNote,
} from "../data-model/busniessOfficeNote";
import { TemplateValidatorIssue } from "../data-model/templateValidatorModel";

/** QC issues keyed by report_identifier only. */
export type WritebackQcByReportIdMap = Record<string, TemplateValidatorIssue[]>;

interface BusniessOfficeNotesContextType {
  allCommentsForUI: AllCommentsProps[];
  globalBoValue: string;
  orderId?: string;
  authStatus?: string;
  manualAuthStatus?: string;
  rawData?: BusniessOfficeNote[];
  hasCptTableAnyError: boolean;
  shouldDisableMarkAsComplete: boolean;
  /**
   * True while the CPT table holds unsaved edits (Save Changes enabled).
   * Kept separate from {@link shouldDisableMarkAsComplete} so the CPT table
   * and the J-code status check don't overwrite each other's gating.
   */
  hasCptUnsavedChanges: boolean;
  /** Writeback QC from template validation, keyed by report_identifier. */
  writebackQcByReportId: WritebackQcByReportIdMap;
  setWritebackQcByReportId: (
    value:
      | WritebackQcByReportIdMap
      | ((prev: WritebackQcByReportIdMap) => WritebackQcByReportIdMap),
  ) => void;
  setAllCommentsForUI: (
    allCommentsForUI:
      AllCommentsProps[] | ((prev: AllCommentsProps[]) => AllCommentsProps[]),
  ) => void;
  setGlobalBoValue: (globalBoValue: string) => void;
  setOrderId: (orderId: string) => void;
  setAuthStatus: (authStatus: string) => void;
  setManualAuthStatus: (manualAuthStatus: string) => void;
  setRawData: (rawData: BusniessOfficeNote[]) => void;
  setHasCptTableAnyError: (hasCptTableAnyError: boolean) => void;
  setShouldDisableMarkAsComplete: (
    shouldDisableMarkAsComplete: boolean,
  ) => void;
  setHasCptUnsavedChanges: (hasCptUnsavedChanges: boolean) => void;
}
const BusniessOfficeNotesContext =
  createContext<BusniessOfficeNotesContextType>({
    allCommentsForUI: [],
    globalBoValue: "",
    setAllCommentsForUI: (
      allCommentsForUI:
        AllCommentsProps[] | ((prev: AllCommentsProps[]) => AllCommentsProps[]),
    ) => {},
    setGlobalBoValue: () => {},
    setOrderId: () => {},
    orderId: "",
    authStatus: "",
    setAuthStatus: () => {},
    manualAuthStatus: "",
    setManualAuthStatus: () => {},
    setRawData: () => {},
    rawData: [],
    hasCptTableAnyError: false,
    setHasCptTableAnyError: () => {},
    shouldDisableMarkAsComplete: false,
    setShouldDisableMarkAsComplete: () => {},
    hasCptUnsavedChanges: false,
    setHasCptUnsavedChanges: () => {},
    writebackQcByReportId: {},
    setWritebackQcByReportId: () => {},
  });

export const useBusniessOfficeNotesContext = () => {
  const context = useContext(BusniessOfficeNotesContext);
  if (!context) {
    throw new Error(
      "useBusniessOfficeNotesContext must be used within a BusniessOfficeNotesContextProvider",
    );
  }
  return context;
};

export const BusniessOfficeNotesContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [allCommentsForUI, setAllCommentsForUI] = useState<AllCommentsProps[]>(
    [],
  );
  const [globalBoValue, setGlobalBoValue] = useState<string>("");
  const [orderId, setOrderId] = useState<string>("");
  const [authStatus, setAuthStatus] = useState<string>("");
  const [manualAuthStatus, setManualAuthStatus] = useState<string>("");
  const [rawData, setRawData] = useState<BusniessOfficeNote[]>([]);
  const [hasCptTableAnyError, setHasCptTableAnyError] =
    useState<boolean>(false);
  const [shouldDisableMarkAsComplete, setShouldDisableMarkAsComplete] =
    useState<boolean>(false);
  const [hasCptUnsavedChanges, setHasCptUnsavedChanges] =
    useState<boolean>(false);
  const [writebackQcByReportId, setWritebackQcByReportId] =
    useState<WritebackQcByReportIdMap>({});
  return (
    <BusniessOfficeNotesContext.Provider
      value={{
        allCommentsForUI,
        setAllCommentsForUI: (
          allCommentsForUI:
            | AllCommentsProps[]
            | ((prev: AllCommentsProps[]) => AllCommentsProps[]),
        ) => {
          setAllCommentsForUI(allCommentsForUI);
        },
        globalBoValue,
        setGlobalBoValue: (globalBoValue: string) => {
          setGlobalBoValue(globalBoValue);
        },
        orderId,
        setOrderId: (orderId: string) => {
          setOrderId(orderId);
        },
        authStatus,
        setAuthStatus: (authStatus: string) => {
          setAuthStatus(authStatus);
        },
        manualAuthStatus,
        setManualAuthStatus: (manualAuthStatus: string) => {
          setManualAuthStatus(manualAuthStatus);
        },
        rawData,
        setRawData: (rawData: BusniessOfficeNote[]) => {
          setRawData(rawData);
        },
        hasCptTableAnyError,
        setHasCptTableAnyError: (hasCptTableAnyError: boolean) => {
          setHasCptTableAnyError(hasCptTableAnyError);
        },
        shouldDisableMarkAsComplete,
        setShouldDisableMarkAsComplete: (
          shouldDisableMarkAsComplete: boolean,
        ) => {
          setShouldDisableMarkAsComplete(shouldDisableMarkAsComplete);
        },
        hasCptUnsavedChanges,
        // Passed through unwrapped so the identity stays stable across renders:
        // `useCptTableState` depends on it inside an effect with an unmount reset.
        setHasCptUnsavedChanges,
        writebackQcByReportId,
        setWritebackQcByReportId,
      }}
    >
      {children}
    </BusniessOfficeNotesContext.Provider>
  );
};
